import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { tokenStorage } from '../../api/client';

interface Cue {
  startMs: number;
  endMs: number;
  x: number;
  y: number;
  width: number;
  height: number;
  /// File name of the picture beside the index. Empty for a cue of words.
  image: string;
  text?: string | null;
}

interface Index {
  width: number;
  height: number;
  cues: Cue[];
  /// Whether this is the whole track. False while a session is still drawing it, which is the
  /// case for a channel and for any film the server cannot cheaply read twice.
  isComplete?: boolean;
}

/// How long a track that cannot be shown says so before it gets out of the way of the film.
const UNAVAILABLE_NOTICE_MS = 5000;

/// Subtitles, fetched from the server and laid over the film by this player.
///
/// Not a track element, for two reasons. A transcode's own clock begins at nought wherever in
/// the film it was opened, and every cue the server hands over is on the film's clock - a
/// track element has no way to be told the difference, so a film resumed an hour in showed
/// every line an hour late, which is to say never. And a film the server cannot cheaply read
/// twice - one read out of Usenet - has no WebVTT to give at all: its words are drawn as the
/// film plays and arrive through the same growing index its pictures do.
///
/// So words and pictures are drawn here, both against where the film is rather than where the
/// stream is. Pictures are positioned against the screen they were drawn for, and the film is
/// letterboxed into whatever box the player has: both are scaled by the same amount, or the
/// words walk off the bottom of the scene.
export default function Subtitles({
  base,
  textFirst,
  video,
  streamStart,
}: {
  /// Where the track is, without a trailing slash or an extension.
  base: string;
  /// Whether to ask for WebVTT before the index. A channel has no WebVTT route: everything it
  /// has is drawn as it goes past.
  textFirst: boolean;
  video: React.RefObject<HTMLVideoElement | null>;
  /// Where in the film the stream begins, in seconds.
  streamStart: React.RefObject<number>;
}) {
  const { t } = useTranslation();

  const [index, setIndex] = useState<Index>();
  const [showing, setShowing] = useState<Cue>();
  const [unavailable, setUnavailable] = useState(false);

  /// Where the film actually is inside the player: it is fitted whole into the box, so there
  /// is black above and below it or beside it, and the subtitles belong on the film.
  const [frame, setFrame] = useState({ left: 0, top: 0, width: 0, height: 0 });

  const token = tokenStorage.read();
  const query = token ? `?token=${encodeURIComponent(token)}` : '';

  // Nothing is cleared here: the component is keyed on the track, so choosing another one
  // mounts a new one rather than reusing this with the wrong cues in it.
  useEffect(() => {
    let current = true;
    let again: number | undefined;

    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    const stop = () => {
      if (again !== undefined) {
        window.clearInterval(again);
        again = undefined;
      }
    };

    const loadIndex = () =>
      fetch(`${base}/index.json`, { headers })
        .then((response) => {
          // A format that is neither words the server can read nor pictures it can draw.
          // Asking again gets the same answer.
          if (response.status === 415) {
            stop();

            if (current) {
              setUnavailable(true);
            }

            return undefined;
          }

          return response.ok ? (response.json() as Promise<Index>) : undefined;
        })
        .then((loaded) => {
          if (!current || !loaded) {
            return;
          }

          setIndex(loaded);

          // The server says whether this is the whole track. It is not, for anything drawn
          // while it plays, and asking once for one of those gets the handful of cues that had
          // been drawn by then and never another.
          if (loaded.isComplete === true) {
            stop();
          }
        })
        .catch(() => undefined);

    const followIndex = () => {
      void loadIndex();

      // Often enough that a line appears with the words rather than after them, rarely enough
      // that it is nothing next to the segments being fetched beside it.
      again = window.setInterval(() => void loadIndex(), 2000);
    };

    if (textFirst) {
      fetch(`${base}.vtt`, { headers })
        .then(async (response) => {
          if (!current) {
            return;
          }

          if (response.ok) {
            const cues = parseWebVtt(await response.text());

            if (current) {
              setIndex({ width: 0, height: 0, cues, isComplete: true });
            }

            return;
          }

          // Pictures after all, or words in a source the server will not read a second time.
          // Either way they are in the index.
          if (response.status === 415) {
            followIndex();

            return;
          }

          setUnavailable(true);
        })
        .catch(() => {
          if (current) {
            setUnavailable(true);
          }
        });
    } else {
      followIndex();
    }

    return () => {
      current = false;
      stop();
    };
  }, [base, textFirst, token]);

  useEffect(() => {
    if (!unavailable) {
      return undefined;
    }

    const timer = window.setTimeout(() => setUnavailable(false), UNAVAILABLE_NOTICE_MS);

    return () => window.clearTimeout(timer);
  }, [unavailable]);

  useEffect(() => {
    const element = video.current;

    if (!element || (!index && !unavailable)) {
      return undefined;
    }

    /// Which cue belongs to this moment, and where the film is on screen. Both are worked out
    /// on every frame the browser draws: the player can be resized, and a film that was
    /// pillarboxed a moment ago is not any more.
    const draw = () => {
      if (index) {
        const at = (streamStart.current + element.currentTime) * 1000;

        setShowing(index.cues.find((cue) => at >= cue.startMs && at < cue.endMs));
      }

      const box = element.getBoundingClientRect();
      const source = element.videoWidth / element.videoHeight;

      if (!Number.isFinite(source) || source <= 0) {
        return;
      }

      // object-fit: contain - the whole frame inside the box, letterboxed or pillarboxed.
      const scale = Math.min(box.width / element.videoWidth, box.height / element.videoHeight);
      const width = element.videoWidth * scale;
      const height = element.videoHeight * scale;

      const next = {
        left: (box.width - width) / 2,
        top: (box.height - height) / 2,
        width,
        height,
      };

      // Only when it has actually moved. This runs on every frame the browser draws, and a
      // new object every time would be sixty renders a second to say nothing changed.
      setFrame((previous) =>
        previous.left === next.left
        && previous.top === next.top
        && previous.width === next.width
        && previous.height === next.height
          ? previous
          : next,
      );
    };

    // Scheduled rather than called: the first pass belongs to the next frame the browser
    // draws, not to the render that set this up.
    let frameRequest = requestAnimationFrame(function tick() {
      draw();
      frameRequest = requestAnimationFrame(tick);
    });

    return () => cancelAnimationFrame(frameRequest);
  }, [index, streamStart, unavailable, video]);

  if (frame.width === 0) {
    return null;
  }

  if (unavailable) {
    return <Words frame={frame} text={t('player.subtitlesUnavailable')} italic />;
  }

  if (!index || !showing) {
    return null;
  }

  if (showing.text) {
    return <Words frame={frame} text={showing.text} />;
  }

  // A channel's index exists before its first cue has been decoded, and until then it does not
  // know the screen it is drawn against. Nothing is laid over the film until it does, because
  // dividing by that nought puts the words somewhere nobody has a monitor.
  if (!showing.image || index.width <= 0) {
    return null;
  }

  const scale = frame.width / index.width;

  return (
    <Box
      component="img"
      src={`${base}/${showing.image}${query}`}
      alt=""
      sx={{
        position: 'absolute',
        pointerEvents: 'none',
        left: frame.left + showing.x * scale,
        top: frame.top + showing.y * scale,
        width: showing.width * scale,
        height: showing.height * scale,
      }}
    />
  );
}

/// A line of words at the foot of the film, sized to the film rather than to the window.
function Words({
  frame,
  text,
  italic = false,
}: {
  frame: { left: number; top: number; width: number; height: number };
  text: string;
  italic?: boolean;
}) {
  return (
    <Box
      sx={{
        position: 'absolute',
        pointerEvents: 'none',
        // The film is centred in the box, so its start edge is the same distance in from either
        // side and this holds whichever way the page reads.
        insetInlineStart: frame.left,
        top: frame.top,
        width: frame.width,
        height: frame.height,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        paddingBottom: `${frame.height * 0.06}px`,
        boxSizing: 'border-box',
      }}
    >
      <Box
        component="span"
        dir="auto"
        sx={{
          maxWidth: '90%',
          whiteSpace: 'pre-line',
          textAlign: 'center',
          fontSize: `${Math.max(frame.height * 0.05, 14)}px`,
          fontStyle: italic ? 'italic' : 'normal',
          lineHeight: 1.3,
          color: 'common.white',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          borderRadius: 1,
          paddingInline: '0.4em',
          paddingBlock: '0.1em',
        }}
      >
        {text}
      </Box>
    </Box>
  );
}

/// The cues of a WebVTT file, on the film's clock as the server wrote them.
///
/// Only what the server's own conversion writes: a timing line and the words under it. Markup
/// inside a cue - italics, voices, karaoke timestamps - is reduced to its words, and entities
/// are decoded by an inert document rather than by hand.
function parseWebVtt(content: string): Cue[] {
  const cues: Cue[] = [];
  const reader = new DOMParser();

  for (const block of content.replace(/\r\n?/g, '\n').split(/\n{2,}/)) {
    const lines = block.split('\n');
    const timing = lines.findIndex((line) => line.includes('-->'));

    if (timing < 0) {
      continue;
    }

    const [start, end] = lines[timing].split('-->').map((part) => milliseconds(part.trim().split(/\s+/)[0]));

    if (start === undefined || end === undefined) {
      continue;
    }

    const words = lines.slice(timing + 1).join('\n');
    const text = reader.parseFromString(words, 'text/html').body.textContent?.trim();

    if (text) {
      cues.push({ startMs: start, endMs: end, x: 0, y: 0, width: 0, height: 0, image: '', text });
    }
  }

  return cues;
}

/// A WebVTT timestamp - `01:02:03.456`, or `02:03.456` without the hours - in milliseconds.
function milliseconds(stamp: string | undefined): number | undefined {
  const match = stamp ? /^(?:(\d+):)?(\d{1,2}):(\d{2})[.,](\d{3})$/.exec(stamp) : null;

  if (!match) {
    return undefined;
  }

  const [, hours = '0', minutes, seconds, fraction] = match;

  return ((Number(hours) * 60 + Number(minutes)) * 60 + Number(seconds)) * 1000 + Number(fraction);
}
